/**
 * @description
 * Component that handles OAuth success callback and processes JWT tokens.
 * This component is specifically designed to handle the OAuth redirect flow
 * without race conditions with protected routes.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 9, 2025
 * @see admin/src/worker/routes/auth.ts
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { storeAuthToken } from '../../../lib/auth';
import { useAuth } from '../../hooks/useAuth';

/**
 * AuthSuccess component for processing OAuth tokens
 */
export function AuthSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processAuth = async () => {
      const token = searchParams.get('token');
      const authError = searchParams.get('error');

      if (authError) {
        setError(authError);
        setTimeout(() => navigate('/login', { replace: true }), 2000);
        return;
      }

      if (!token) {
        setError('No authentication token provided');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
        return;
      }

      try {
        // Store token
        storeAuthToken(token);
        
        // Refresh auth context
        await refreshSession();
        
        // Small delay to ensure context updates
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Redirect to dashboard
        navigate('/', { replace: true });
      } catch (err) {
        console.error('Token processing error:', err);
        setError('Failed to process authentication');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      }
    };

    processAuth();
  }, [searchParams, navigate, refreshSession]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="text-xl font-bold text-destructive mb-2">Authentication Error</h2>
          <p className="text-muted-foreground">{error}</p>
          <p className="text-sm text-muted-foreground mt-2">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
        <h2 className="text-xl font-bold">Completing Sign In</h2>
        <p className="text-muted-foreground">Please wait...</p>
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
